/**
 * @param {!Function} requestFn The async/request function to call, which returns a Promise<?>.
 * @param {string=} testCase
 * @return {{
 *   status: (string | undefined),
 *   data: (? | undefined),
 *   refetch: function(...*),
 *   abort: function(),
 * }}
 */
export function useFetch(requestFn, testCase='') {
  const abortController = React.useRef(new AbortController());
  const [state, dispatch] = React.useReducer(onDispatch, {
    status: undefined,
    data: undefined,
  });

  const refetch = React.useCallback((...params) => {
    abortController.current.abort();
    dispatch({type: 'loading'});

    abortController.current = new AbortController();
    requestFn(...params, testCase, abortController.current.signal)
      .then(data => dispatch({type: 'success', data}))
      .catch(aborted => !aborted && dispatch({type: 'error'}));
  }, [requestFn, testCase]);

  const abort = React.useCallback(() => {
    abortController.current.abort();
    dispatch({type: 'aborted'});
  }, []);

  React.useEffect(() => {
    const currentAbortController = abortController.current;
    return () => currentAbortController.abort();
  }, []);

  return {
    status: state.status,
    data: state.data,
    refetch,
    abort,
  };
}

function onDispatch(state, action) {
  switch (action.type) {
    case 'loading':
      return {...state, status: 'loading'};
    case 'success':
      return {status: 'success', data: action.data};
    case 'error':
      return {status: 'error', data: undefined};
    case 'aborted':
      if (state.status !== 'loading') {
        return state;
      } else if (state.data !== undefined) {
        return {...state, status: 'success'};
      } else {
        return {status: undefined, data: undefined};
      }
    default:
      throw new Error(`unhandled useFetch action '${action.type}'`);
  }
}
